import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import {
  User,
  Plane,
  FileText,
  Calendar,
  Clock,
  MapPin,
  CreditCard,
  Download,
  Eye,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Mail,
  Phone,
  Receipt,
  Key,
  Edit,
  Save,
  X,
  Radio
} from 'lucide-react';
import axios from 'axios';
import { downloadETicketPDF, downloadInvoicePDF } from '../utils/pdfGenerator';
import { FlightStatusTracker } from './FlightStatusTracker';
import { Money } from '../contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CustomerDashboard = ({ user, onBack, onManageBooking }) => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [generatingTicket, setGeneratingTicket] = useState(null);
  const [trackingPnr, setTrackingPnr] = useState(null); // For flight status tracking
  
  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (user?.email) {
      fetchUserBookings();
    }
  }, [user]);

  // Initialize profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const fetchUserBookings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/bookings`, {
        params: { email: user.email },
        withCredentials: true
      });
      
      if (response.data.success) {
        setBookings(response.data.bookings || []);
      } else {
        setError('Failed to load bookings');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load your bookings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/user/profile`, {
        email: user.email,
        name: profileData.name,
        phone: profileData.phone
      });
      
      if (response.data.success) {
        setIsEditingProfile(false);
        // Update local user data if needed
        alert('Profile updated successfully!');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/change-password`, {
        email: user.email,
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      if (response.data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(response.data.message || 'Failed to change password');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.detail || 'Failed to change password. Please check your current password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const match = time.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : time.substring(0, 5);
  };

  const getPaymentStatus = (booking) => {
    // Determine payment status based on booking data
    if (booking.payment_status) return booking.payment_status;
    if (booking.status === 'CONFIRMED') return 'PAID';
    if (booking.status === 'PENDING') return 'PENDING';
    return 'PAID'; // Default for confirmed bookings
  };

  // Use centralized PDF utility for invoice generation
  const handleGenerateInvoice = async (booking) => {
    setGeneratingInvoice(booking.pnr);
    try {
      await downloadInvoicePDF(booking);
    } catch (err) {
      console.error('Invoice generation error:', err);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  // Use centralized PDF utility for e-ticket generation
  const handleGenerateETicket = async (booking) => {
    setGeneratingTicket(booking.pnr);
    try {
      await downloadETicketPDF(booking);
    } catch (err) {
      console.error('E-Ticket generation error:', err);
    } finally {
      setGeneratingTicket(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="text-slate-600 hover:text-brand-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {/* Profile Header */}
        <Card className="mb-6 border-0 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-brand-600 via-purple-600 to-blue-600 p-6 text-white">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-20 h-20 rounded-full border-4 border-white/30 shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="h-10 w-10 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{user?.name || 'Customer'}</h1>
                <p className="text-white/80">{user?.email}</p>
                <Badge className="mt-2 bg-white/20 text-white">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Verified Account
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 gap-2 bg-white p-2 rounded-xl shadow-md">
            <TabsTrigger 
              value="bookings" 
              className="data-[state=active]:bg-brand-500 data-[state=active]:text-white rounded-lg py-3"
            >
              <Plane className="h-4 w-4 mr-2" />
              My Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="invoices"
              className="data-[state=active]:bg-brand-500 data-[state=active]:text-white rounded-lg py-3"
            >
              <FileText className="h-4 w-4 mr-2" />
              Invoices
            </TabsTrigger>
            <TabsTrigger 
              value="profile"
              className="data-[state=active]:bg-brand-500 data-[state=active]:text-white rounded-lg py-3"
            >
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {isLoading ? (
              <Card className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-500" />
                <p className="mt-4 text-slate-600">Loading your bookings...</p>
              </Card>
            ) : error ? (
              <Card className="p-8 text-center border-red-200 bg-red-50">
                <AlertCircle className="h-8 w-8 mx-auto text-red-500" />
                <p className="mt-4 text-red-700">{error}</p>
                <Button onClick={fetchUserBookings} className="mt-4">
                  Try Again
                </Button>
              </Card>
            ) : bookings.length === 0 ? (
              <Card className="p-8 text-center">
                <Plane className="h-12 w-12 mx-auto text-slate-300" />
                <h3 className="mt-4 text-lg font-semibold text-slate-700">No Bookings Yet</h3>
                <p className="text-slate-500 mt-2">Your flight bookings will appear here once you make a reservation.</p>
                <Button onClick={onBack} className="mt-4 bg-brand-500 hover:bg-brand-600">
                  Search Flights
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => {
                  const flight = booking.flight_data || booking.flight || {};
                  const paymentStatus = getPaymentStatus(booking);
                  
                  return (
                    <Card key={booking.pnr} className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                      <div className="flex flex-col lg:flex-row">
                        {/* Booking Info */}
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 uppercase">Booking Reference</span>
                              </div>
                              <span className="text-2xl font-bold text-brand-600 font-mono tracking-wider">
                                {booking.pnr}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Badge className={booking.status === 'CONFIRMED' ? 'bg-emerald-500' : 'bg-amber-500'}>
                                {booking.status || 'CONFIRMED'}
                              </Badge>
                              <Badge className={paymentStatus === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                <CreditCard className="h-3 w-3 mr-1" />
                                {paymentStatus}
                              </Badge>
                            </div>
                          </div>

                          {/* Flight Route */}
                          <div className="flex items-center gap-4 mb-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-slate-800">
                                {flight.from || flight.origin || 'N/A'}
                              </p>
                              <p className="text-sm text-slate-500">{formatTime(flight.departure_time)}</p>
                            </div>
                            <div className="flex-1 flex items-center">
                              <div className="h-0.5 flex-1 bg-gradient-to-r from-brand-500 to-purple-500"></div>
                              <Plane className="h-5 w-5 text-brand-500 mx-2" />
                              <div className="h-0.5 flex-1 bg-gradient-to-r from-purple-500 to-brand-500"></div>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-slate-800">
                                {flight.to || flight.destination || 'N/A'}
                              </p>
                              <p className="text-sm text-slate-500">{formatTime(flight.arrival_time)}</p>
                            </div>
                          </div>

                          {/* Flight Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Calendar className="h-4 w-4 text-brand-500" />
                              <span>{formatDate(flight.departure_date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Clock className="h-4 w-4 text-purple-500" />
                              <span>{flight.duration || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <User className="h-4 w-4 text-blue-500" />
                              <span>{booking.passengers?.length || 1} Passenger(s)</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Plane className="h-4 w-4 text-emerald-500" />
                              <span>{flight.airline || 'Airline'}</span>
                            </div>
                          </div>

                          {/* Return Flight */}
                          {flight.return_departure_time && (
                            <div className="mt-4 pt-4 border-t border-slate-100">
                              <p className="text-xs text-slate-500 uppercase mb-2">Return Flight</p>
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Calendar className="h-4 w-4 text-brand-500" />
                                <span>{formatDate(flight.return_departure_date)} at {formatTime(flight.return_departure_time)}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Price & Actions */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex flex-col justify-between border-l lg:w-64">
                          <div>
                            <p className="text-xs text-slate-500 uppercase">Total Price</p>
                            <p className="text-3xl font-bold text-brand-600">
                              <Money gbp={(booking.total_price || 0).toFixed(2)} />
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Booked: {formatDate(booking.created_at)}
                            </p>
                          </div>
                          <div className="mt-4 space-y-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => handleGenerateInvoice(booking)}
                              disabled={generatingInvoice === booking.pnr}
                            >
                              {generatingInvoice === booking.pnr ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Receipt className="h-4 w-4 mr-2" />
                              )}
                              Invoice
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full"
                              onClick={() => handleGenerateETicket(booking)}
                              disabled={generatingTicket === booking.pnr}
                            >
                              {generatingTicket === booking.pnr ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4 mr-2" />
                              )}
                              E-Ticket
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full border-green-500 text-green-600 hover:bg-green-50"
                              onClick={() => setTrackingPnr(booking.pnr)}
                              data-testid={`track-flight-${booking.pnr}`}
                            >
                              <Radio className="h-4 w-4 mr-2" />
                              Track Flight
                            </Button>
                            <Button 
                              size="sm" 
                              className="w-full bg-brand-500 hover:bg-brand-600"
                              onClick={onManageBooking}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            {isLoading ? (
              <Card className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-500" />
                <p className="mt-4 text-slate-600">Loading invoices...</p>
              </Card>
            ) : bookings.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-slate-300" />
                <h3 className="mt-4 text-lg font-semibold text-slate-700">No Invoices</h3>
                <p className="text-slate-500 mt-2">Invoices will appear here after you make a booking.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => {
                  const paymentStatus = getPaymentStatus(booking);
                  const flight = booking.flight_data || booking.flight || {};
                  
                  return (
                    <Card key={booking.pnr} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-brand-100 rounded-lg">
                              <Receipt className="h-6 w-6 text-brand-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">Invoice #{booking.pnr}</p>
                              <p className="text-sm text-slate-500">
                                {flight.from || 'N/A'} → {flight.to || 'N/A'} • {formatDate(booking.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-bold text-brand-600">
                                <Money gbp={(booking.total_price || 0).toFixed(2)} />
                              </p>
                              <Badge className={paymentStatus === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {paymentStatus}
                              </Badge>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleGenerateInvoice(booking)}
                                disabled={generatingInvoice === booking.pnr}
                                title="Download Invoice"
                              >
                                {generatingInvoice === booking.pnr ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Receipt className="h-4 w-4" />
                                )}
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleGenerateETicket(booking)}
                                disabled={generatingTicket === booking.pnr}
                                title="Download E-Ticket"
                              >
                                {generatingTicket === booking.pnr ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-brand-500" />
                      Profile Information
                    </CardTitle>
                    <CardDescription>Your account details</CardDescription>
                  </div>
                  {!isEditingProfile && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsEditingProfile(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Full Name</label>
                    {isEditingProfile ? (
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        placeholder="Enter your name"
                      />
                    ) : (
                      <div className="p-3 bg-slate-50 rounded-lg border">
                        <p className="text-slate-800">{user?.name || 'Not provided'}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Email Address</label>
                    <div className="p-3 bg-slate-50 rounded-lg border flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <p className="text-slate-800">{user?.email}</p>
                    </div>
                    <p className="text-xs text-slate-500">Email cannot be changed</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Phone Number</label>
                    {isEditingProfile ? (
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        placeholder="+44 7XXX XXXXXX"
                      />
                    ) : (
                      <div className="p-3 bg-slate-50 rounded-lg border flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <p className="text-slate-800">{user?.phone || 'Not provided'}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Edit Profile Buttons */}
                {isEditingProfile && (
                  <div className="flex gap-3">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileData({ name: user?.name || '', phone: user?.phone || '' });
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateProfile}
                      disabled={savingProfile}
                      className="bg-brand-600 hover:bg-brand-700"
                    >
                      {savingProfile ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" /> Save Changes</>
                      )}
                    </Button>
                  </div>
                )}

                <Separator />

                {/* Password Change Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Key className="h-5 w-5 text-slate-600" />
                    Password & Security
                  </h3>
                  
                  {user?.auth_type === 'google' ? (
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <p className="text-slate-600 text-sm">
                        You signed in with Google. Password management is handled through your Google account.
                      </p>
                    </div>
                  ) : !showPasswordChange ? (
                    <Button 
                      variant="outline"
                      onClick={() => setShowPasswordChange(true)}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Change Password
                    </Button>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-lg border space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Current Password</label>
                        <Input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          placeholder="Enter current password"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">New Password</label>
                        <Input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          placeholder="Enter new password"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
                        <Input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                          placeholder="Confirm new password"
                        />
                      </div>
                      
                      {passwordError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          {passwordError}
                        </div>
                      )}
                      
                      {passwordSuccess && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          {passwordSuccess}
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setShowPasswordChange(false);
                            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            setPasswordError('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleChangePassword}
                          disabled={changingPassword}
                          className="bg-brand-600 hover:bg-brand-700"
                        >
                          {changingPassword ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Changing...</>
                          ) : (
                            'Change Password'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-4"
                      onClick={onManageBooking}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-brand-100 rounded-lg">
                          <Plane className="h-5 w-5 text-brand-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold">Manage Booking</p>
                          <p className="text-xs text-slate-500">View or modify your bookings</p>
                        </div>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-4"
                      onClick={onBack}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <MapPin className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold">Search Flights</p>
                          <p className="text-xs text-slate-500">Find and book new flights</p>
                        </div>
                      </div>
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="text-sm text-slate-500">
                  <p>Account created via {user?.auth_type === 'google' ? 'Google Sign-In' : 'Email'}</p>
                  <p className="mt-1">User ID: {user?.user_id}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Flight Status Tracking Modal */}
      {trackingPnr && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Radio className="h-5 w-5 text-green-600" />
                Live Flight Status
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTrackingPnr(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4">
              <FlightStatusTracker pnr={trackingPnr} onClose={() => setTrackingPnr(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
